import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Folder, File, FileText, FileImage, FileSpreadsheet,
  Upload, FolderPlus, Download, Trash2, Pencil, ExternalLink,
  ChevronRight, Loader2, AlertCircle, RefreshCw, HardDrive, X, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function getMimeColor(mime) {
  if (!mime) return 'text-gray-400';
  if (mime.includes('pdf')) return 'text-red-500';
  if (mime.includes('image')) return 'text-purple-500';
  if (mime.includes('sheet') || mime.includes('excel') || mime.includes('csv')) return 'text-green-600';
  if (mime.includes('word') || mime.includes('document')) return 'text-blue-600';
  if (mime.includes('presentation') || mime.includes('powerpoint')) return 'text-orange-500';
  if (mime.includes('zip') || mime.includes('rar') || mime.includes('7z')) return 'text-yellow-600';
  return 'text-gray-500';
}

function FileIcon({ item, size = 'w-10 h-10' }) {
  if (item.type === 'folder') return <Folder className={`${size} text-yellow-400`} fill="currentColor" />;
  const mime = item.mime_type || '';
  const cls = `${size} ${getMimeColor(mime)}`;
  if (mime.includes('pdf')) return <FileText className={cls} />;
  if (mime.includes('image')) return <FileImage className={cls} />;
  if (mime.includes('sheet') || mime.includes('excel') || mime.includes('csv')) return <FileSpreadsheet className={cls} />;
  if (mime.includes('word') || mime.includes('document')) return <FileText className={cls} />;
  return <File className={cls} />;
}

// ── Rename inline component ───────────────────────────────────────────────────

function RenameInput({ item, onSave, onCancel }) {
  const [val, setVal] = useState(item.name);
  const inputRef = useRef();
  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);
  return (
    <div className="flex items-center gap-1 w-full" onClick={e => e.stopPropagation()}>
      <Input
        ref={inputRef}
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') onSave(val);
          if (e.key === 'Escape') onCancel();
        }}
        className="h-6 text-xs px-1 py-0 flex-1"
      />
      <button onClick={() => onSave(val)} className="p-0.5 text-green-600 hover:text-green-700"><Check className="w-3.5 h-3.5" /></button>
      <button onClick={onCancel} className="p-0.5 text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

// ── New Folder modal ──────────────────────────────────────────────────────────

function NewFolderModal({ onClose, onCreate }) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-xl p-6 w-80">
        <h3 className="font-semibold text-gray-800 mb-4">New Folder</h3>
        <Input
          autoFocus
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && name.trim()) onCreate(name.trim(), setLoading); }}
          placeholder="Folder name"
          className="mb-4"
          data-testid="new-folder-input"
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            disabled={!name.trim() || loading}
            onClick={() => onCreate(name.trim(), setLoading)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="new-folder-create-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Delete confirm modal ──────────────────────────────────────────────────────

function DeleteModal({ item, onClose, onDelete }) {
  const [loading, setLoading] = useState(false);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-xl p-6 w-96">
        <div className="flex items-start gap-3 mb-4">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-gray-800">Delete {item.type === 'folder' ? 'Folder' : 'File'}?</p>
            <p className="text-sm text-gray-500 mt-1">
              "<span className="font-medium">{item.name}</span>" will be permanently deleted from Google Drive.
              {item.type === 'folder' && ' All files inside will also be deleted.'}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm" variant="destructive"
            disabled={loading}
            onClick={() => onDelete(item, setLoading)}
            data-testid="confirm-delete-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const DriveFileBrowser = () => {
  const { toast } = useToast();
  const fileInputRef = useRef();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [driveConnected, setDriveConnected] = useState(true);
  const [notInitialized, setNotInitialized] = useState(false);

  // Navigation state
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: 'LogisticsHub Files' }]);
  const currentFolderId = breadcrumbs[breadcrumbs.length - 1].id;

  // Modals
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Rename state — { id, name } of item being renamed
  const [renaming, setRenaming] = useState(null);

  // Drag-over for upload
  const [dragOver, setDragOver] = useState(false);

  const fetchContents = useCallback(async (folderId) => {
    setLoading(true);
    try {
      const url = folderId
        ? `${backendUrl}/api/drive/browse?folder_id=${folderId}`
        : `${backendUrl}/api/drive/browse`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.status === 400) { setDriveConnected(false); return; }
      if (!res.ok) throw new Error('Failed to load folder contents');
      const data = await res.json();
      if (data.root_not_initialized) { setNotInitialized(true); setItems([]); return; }
      setNotInitialized(false);
      setItems(data.items || []);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContents(currentFolderId); }, [currentFolderId]);

  const navigateInto = (folder) => {
    setBreadcrumbs(prev => [...prev, { id: folder.id, name: folder.name }]);
  };

  const navigateToCrumb = (idx) => {
    setBreadcrumbs(prev => prev.slice(0, idx + 1));
  };

  // ── Upload ────────────────────────────────────────────────────────────────

  const handleUploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (!currentFolderId) {
      toast({ title: 'Cannot upload here', description: 'Navigate into a folder first, or create one.', variant: 'destructive' });
      return;
    }
    setUploading(true);
    let successCount = 0;
    for (const file of Array.from(files)) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${backendUrl}/api/drive/browse/upload?folder_id=${currentFolderId}`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: formData,
        });
        if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Upload failed'); }
        const created = await res.json();
        setItems(prev => [...prev, created]);
        successCount++;
      } catch (e) {
        toast({ title: `Upload failed: ${file.name}`, description: e.message, variant: 'destructive' });
      }
    }
    setUploading(false);
    if (successCount > 0) toast({ title: 'Uploaded', description: `${successCount} file(s) uploaded successfully` });
  };

  // ── Create Folder ─────────────────────────────────────────────────────────

  const handleCreateFolder = async (name, setLoading) => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/drive/folders/create`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, parent_id: currentFolderId }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      const folder = await res.json();
      setItems(prev => [folder, ...prev]);
      setShowNewFolder(false);
      toast({ title: 'Folder created', description: `"${name}" created` });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // ── Rename ─────────────────────────────────────────────────────────────────

  const handleRename = async (item, newName) => {
    if (!newName.trim() || newName === item.name) { setRenaming(null); return; }
    try {
      const res = await fetch(`${backendUrl}/api/drive/items/${item.id}/rename`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim() }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Rename failed'); }
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, name: newName.trim() } : i));
      toast({ title: 'Renamed', description: `"${newName}" saved` });
    } catch (e) {
      toast({ title: 'Rename failed', description: e.message, variant: 'destructive' });
    } finally {
      setRenaming(null);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDelete = async (item, setLoading) => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/drive/items/${item.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Delete failed'); }
      setItems(prev => prev.filter(i => i.id !== item.id));
      setDeleteTarget(null);
      toast({ title: 'Deleted', description: `"${item.name}" removed from Drive` });
    } catch (e) {
      toast({ title: 'Delete failed', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // ── Download ───────────────────────────────────────────────────────────────

  const handleDownload = (item) => {
    const token = localStorage.getItem('auth_token');
    window.open(`${backendUrl}/api/drive/files/${item.id}/download?token=${token}`, '_blank');
  };

  // ── Drag + Drop ────────────────────────────────────────────────────────────

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUploadFiles(e.dataTransfer.files);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!driveConnected) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4 text-gray-500">
        <HardDrive className="w-12 h-12 text-gray-300" />
        <p className="text-lg font-semibold text-gray-700">Google Drive not connected</p>
        <p className="text-sm">Go to <span className="font-medium text-blue-600">Settings → Drive</span> to connect your Google account.</p>
      </div>
    );
  }

  const folders = items.filter(i => i.type === 'folder');
  const files = items.filter(i => i.type === 'file');

  return (
    <div
      className="space-y-4"
      data-testid="drive-browser-page"
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <HardDrive className="w-5 h-5 text-blue-600" />
          <h2 className="text-xl font-bold text-gray-900 font-manrope">Documents</h2>
          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-ibm">Google Drive</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm" variant="outline"
            onClick={() => fetchContents(currentFolderId)}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {currentFolderId && (
            <Button
              size="sm" variant="outline"
              onClick={() => setShowNewFolder(true)}
              data-testid="new-folder-btn"
            >
              <FolderPlus className="w-3.5 h-3.5 mr-1.5" />
              New Folder
            </Button>
          )}
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || !currentFolderId}
            data-testid="upload-btn"
          >
            {uploading
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Uploading...</>
              : <><Upload className="w-3.5 h-3.5 mr-1.5" />Upload</>
            }
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={e => handleUploadFiles(e.target.files)}
          />
        </div>
      </div>

      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm font-ibm flex-wrap" data-testid="breadcrumb">
        {breadcrumbs.map((crumb, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
            <button
              onClick={() => navigateToCrumb(idx)}
              className={`px-1 rounded hover:underline ${
                idx === breadcrumbs.length - 1
                  ? 'text-gray-800 font-semibold cursor-default'
                  : 'text-blue-600 hover:text-blue-700'
              }`}
              data-testid={`crumb-${idx}`}
            >
              {crumb.name}
            </button>
          </React.Fragment>
        ))}
      </nav>

      {/* Drag-over overlay */}
      {dragOver && currentFolderId && (
        <div className="fixed inset-0 bg-blue-600/10 border-4 border-dashed border-blue-400 z-40 flex items-center justify-center pointer-events-none rounded-2xl">
          <p className="text-blue-600 text-xl font-semibold">Drop files to upload</p>
        </div>
      )}

      {/* Content */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

        {loading ? (
          <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            <span className="text-sm font-ibm">Loading...</span>
          </div>
        ) : notInitialized ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
            <HardDrive className="w-12 h-12 text-gray-300" />
            <p className="text-sm font-ibm font-medium text-gray-500">Drive not initialized yet</p>
            <p className="text-xs text-gray-400 text-center max-w-xs">
              Upload a document from any Docket or use Store Documents to create the root folder.
            </p>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Folder className="w-12 h-12 text-gray-200" />
            <p className="text-sm font-ibm text-gray-400">This folder is empty</p>
            {currentFolderId && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowNewFolder(true)}>
                  <FolderPlus className="w-3.5 h-3.5 mr-1" /> New Folder
                </Button>
                <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="w-3.5 h-3.5 mr-1" /> Upload File
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div>
            {/* Folders section */}
            {folders.length > 0 && (
              <div className="px-5 pt-4 pb-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 font-ibm">
                  Folders ({folders.length})
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                  {folders.map(folder => (
                    <div
                      key={folder.id}
                      data-testid={`folder-${folder.id}`}
                      className="group relative flex flex-col items-center gap-1.5 p-3 rounded-xl border border-transparent hover:border-yellow-200 hover:bg-yellow-50 cursor-pointer transition-all"
                      onDoubleClick={() => navigateInto(folder)}
                      onClick={() => navigateInto(folder)}
                    >
                      <Folder className="w-12 h-12 text-yellow-400" fill="currentColor" />

                      {renaming?.id === folder.id ? (
                        <RenameInput
                          item={folder}
                          onSave={newName => handleRename(folder, newName)}
                          onCancel={() => setRenaming(null)}
                        />
                      ) : (
                        <span className="text-xs text-center text-gray-700 font-ibm font-medium leading-tight line-clamp-2 w-full text-center">
                          {folder.name}
                        </span>
                      )}

                      {/* Action buttons */}
                      <div className="absolute top-1.5 right-1.5 hidden group-hover:flex gap-0.5 bg-white rounded-md shadow border border-gray-100 p-0.5">
                        {folder.web_view_link && (
                          <button
                            onClick={e => { e.stopPropagation(); window.open(folder.web_view_link, '_blank'); }}
                            className="p-1 text-gray-400 hover:text-blue-600 rounded"
                            title="Open in Drive"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        )}
                        <button
                          onClick={e => { e.stopPropagation(); setRenaming(folder); }}
                          className="p-1 text-gray-400 hover:text-blue-600 rounded"
                          title="Rename"
                          data-testid={`rename-${folder.id}`}
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); setDeleteTarget(folder); }}
                          className="p-1 text-gray-400 hover:text-red-600 rounded"
                          title="Delete"
                          data-testid={`delete-${folder.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Files section — list view */}
            {files.length > 0 && (
              <div className="px-5 pt-4 pb-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 font-ibm">
                  Files ({files.length})
                </p>
                <div className="rounded-lg border border-gray-100 overflow-hidden">
                  <table className="w-full text-xs font-ibm" data-testid="files-table">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-gray-500 font-semibold w-8"></th>
                        <th className="px-3 py-2 text-left text-gray-500 font-semibold">Name</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-semibold w-24">Size</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-semibold w-40">Modified</th>
                        <th className="px-3 py-2 text-right text-gray-500 font-semibold w-32">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {files.map(file => (
                        <tr key={file.id} className="hover:bg-gray-50 group transition-colors">
                          <td className="px-3 py-2">
                            <FileIcon item={file} size="w-5 h-5" />
                          </td>
                          <td className="px-3 py-2">
                            {renaming?.id === file.id ? (
                              <RenameInput
                                item={file}
                                onSave={newName => handleRename(file, newName)}
                                onCancel={() => setRenaming(null)}
                              />
                            ) : (
                              <span className="text-gray-800 font-medium">{file.name}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-500">{formatSize(file.size)}</td>
                          <td className="px-3 py-2 text-gray-400">{formatDate(file.modified_at)}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center justify-end gap-1">
                              {file.web_view_link && (
                                <button
                                  onClick={() => window.open(file.web_view_link, '_blank')}
                                  className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                                  title="View in Drive"
                                  data-testid={`view-${file.id}`}
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDownload(file)}
                                className="p-1.5 rounded text-gray-400 hover:text-green-600 hover:bg-green-50"
                                title="Download"
                                data-testid={`download-${file.id}`}
                              >
                                <Download className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => setRenaming(file)}
                                className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                                title="Rename"
                                data-testid={`rename-${file.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => setDeleteTarget(file)}
                                className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                                title="Delete"
                                data-testid={`delete-${file.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Upload hint (only when navigated into a folder) */}
      {currentFolderId && !loading && (
        <p className="text-xs text-gray-400 font-ibm text-center">
          Drag and drop files anywhere on the page to upload to the current folder
        </p>
      )}

      {/* Modals */}
      {showNewFolder && (
        <NewFolderModal
          onClose={() => setShowNewFolder(false)}
          onCreate={handleCreateFolder}
        />
      )}
      {deleteTarget && (
        <DeleteModal
          item={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default DriveFileBrowser;
